import type { ApolloError } from '@apollo/client';
import { useEffect } from 'react';

import type { Company } from '@xing-com/crate-common-graphql-types';

import { MAX_LOGGED_OUT_ITEMS } from '../../config';
import { useCompanyData } from '../../hooks/use-company-data/use-company-data';
import { CompanyCard } from '../company-card';
import { CompanyCardContainer } from '../company-card-container';
import { MetaData } from '../meta-data';
import { PageError } from '../page-error';
import { Pagination } from '../pagination';
import { CompanyCardsSkeletonLoggedoutPage } from '../skeleton';
import * as Styled from './company-loggedout.styles';

const CompanyLoggedout = ({
  companies,
  total,
  headerText,
  secondaryHeader,
  authenticated,
  limit,
  activePage,
  changePage,
  loading,
  error,
  companyType,
}: {
  companies: Company[] | undefined;
  total: number | undefined;
  headerText: string;
  authenticated: boolean;
  secondaryHeader?: JSX.Element;
  limit: number;
  activePage: number;
  changePage: any;
  loading: boolean;
  error: ApolloError | undefined;
  companyType: 'industry' | 'most-visited' | 'recently-claimed';
}) => {
  const { addCompanies, globalCompaniesState } = useCompanyData();
  useEffect(() => {
    if (companies) {
      addCompanies({
        companies,
      });
    }
  }, [companies]);

  if (loading) return <CompanyCardsSkeletonLoggedoutPage />;
  if (error) return <PageError />;
  return (
    <>
      <MetaData index title={headerText} description={headerText} />
      <Styled.Headline
        size="medium"
        as="h1"
        className={
          companyType === 'industry' ? 'industry-company-headline' : undefined
        }
      >
        {headerText}
      </Styled.Headline>
      {secondaryHeader}
      <CompanyCardContainer>
        {companies?.map((company, index) => {
          const isFollowing =
            company?.userContext?.followState?.isFollowing || false;

          return (
            <CompanyCard
              key={company?.id}
              company={company}
              buttons={
                authenticated && globalCompaniesState.length > 0
                  ? [
                      <Styled.FollowButton
                        disabled={company?.entityPageId ? false : true}
                        entityPageId={company?.entityPageId || ''}
                        key={company?.entityPageId}
                        isFollowing={isFollowing}
                        companyId={company?.id}
                        position={index}
                      />,
                    ]
                  : []
              }
            />
          );
        })}
      </CompanyCardContainer>
      {companies && (
        <Pagination
          currentPage={activePage}
          perPage={limit}
          total={Math.min(MAX_LOGGED_OUT_ITEMS, total as number)}
          onPageClick={changePage}
        />
      )}
    </>
  );
};

export { CompanyLoggedout };
